// La red debajo de todo: qué se ve cuando React se cae.
//
// ═══════════════════════════════════════════════════════════════════
// POR QUÉ EXISTE, Y POR QUÉ DEBERÍA HABER EXISTIDO ANTES
// ═══════════════════════════════════════════════════════════════════
//
// El 5/9, un usuario nuevo entraba y veía una **página blanca**. Nada:
// ni un mensaje, ni un botón, ni una explicación. La causa fue una
// variable de estado que tapaba una función importada (`pais` en
// PrimerUso.js) y está arreglada. Pero el síntoma no dependía de esa
// causa: **cualquier** error durante el render produce exactamente lo
// mismo, porque React desmonta el árbol entero cuando nadie atrapa la
// excepción.
//
// Sin esto, el próximo error de render —el que sea, en cualquier
// pantalla, el año que viene— vuelve a ser una página en blanco. Y una
// página en blanco no se reporta bien: la persona dice "no me anda" o,
// peor, "se me borró todo", y hay que adivinar.
//
// ═══════════════════════════════════════════════════════════════════
// QUÉ HACE Y QUÉ NO
// ═══════════════════════════════════════════════════════════════════
//
// Muestra un cartel con tres cosas: que el problema es de la app y no
// de sus datos, un botón para recargar, y el detalle técnico plegado
// para que se pueda copiar y pegar.
//
// La primera es la que importa. Alguien que ve una pantalla rota sobre
// su armario asume que perdió el armario. **No perdió nada**: todo está
// en la cuenta, del lado del servidor, y el cartel lo dice con esas
// palabras.
//
// Lo que NO hace: reintentar solo. Un error de render suele repetirse
// con el mismo estado, y un cartel que parpadea y vuelve a romperse es
// peor que uno que se queda quieto. Recargar es del usuario.
//
// ⚠️  NO ATRAPA TODO. Un `ErrorBoundary` de React solo ve lo que pasa
//     durante el render, en los métodos de ciclo de vida y en los
//     constructores. NO ve errores dentro de un `onClick`, ni de un
//     `setTimeout`, ni una promesa rechazada. Para esos está
//     `mirarLosOtrosErrores()` acá abajo, que los manda a la consola
//     con una marca reconocible — no cambia la pantalla, porque una app
//     que sigue andando no tiene que cubrirse de carteles, pero deja
//     rastro en el único lugar donde hoy se puede mirar.

import React from "react";
import { t } from "./i18n";

/* ⚠️  LAS CLAVES VAN LITERALES, Y NO ES ESTILO.
 *
 * La primera versión de esto tenía un ayudante `texto(clave, respaldo)`
 * que envolvía cada `t()` en un try/catch. `test_i18n.js` §8b lo puso
 * rojo en el acto, y tiene razón: con la clave adentro de una variable,
 * el extractor no la ve, y entonces §6b no exige que exista en los once
 * idiomas, §6c no la busca y §6d no compara sus marcadores. Las cinco
 * claves de esta pantalla podrían no existir en ningún idioma y toda la
 * suite quedaría verde.
 *
 * La protección contra un `t()` que reviente —posible, porque este
 * componente es lo último que queda cuando algo se rompió, y lo roto
 * podría ser el propio i18n— está donde tiene que estar: un try/catch
 * alrededor de TODO el render, con un respaldo mínimo y sin traducir.
 * Feo en once idiomas, e infinitamente mejor que una segunda pantalla
 * en blanco. */

export default class Reventon extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    /* Al log, con marca. Hoy es lo único que hay; el día que haya un
     * servicio de errores, esta es la línea que lo llama. */
    console.error("💥 Brujel se rompió al dibujar:", error, info?.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;

    try {
      return this.pantalla();
    } catch (e) {
      /* El respaldo del respaldo. Si llegamos acá es que hasta `t()`
       * está roto: sin traducir, sin clases, sin nada que pueda faltar.
       * Un cartel feo en castellano es lo único que garantiza que la
       * persona vea ALGO y tenga un botón. */
      console.error("💥 Brujel: hasta la pantalla de error se rompió:", e);
      return (
        <div style={{ maxWidth: 560, margin: "48px auto", padding: "0 20px" }}>
          <h1>Algo se rompió de nuestro lado</h1>
          <p>Tu ropa y tus datos están a salvo en tu cuenta.</p>
          <button type="button" onClick={() => window.location.reload()}>
            Recargar
          </button>
        </div>
      );
    }
  }

  pantalla() {
    const detalle = [
      String(this.state.error?.message || this.state.error || ""),
      this.state.error?.stack || "",
    ]
      .filter(Boolean)
      .join("\n");

    return (
      <div className="reventon">
        <h1 className="reventon-titulo">
          {t("reventon.titulo", "Algo se rompió de nuestro lado")}
        </h1>

        {/* LA FRASE QUE IMPORTA. Ver el comentario de arriba: quien ve
            una pantalla rota sobre su armario asume que perdió el
            armario. */}
        <p className="reventon-calma">
          {t(
            "reventon.calma",
            "Tu ropa y tus datos están a salvo en tu cuenta: esto es un error de la aplicación, no de tu información."
          )}
        </p>

        <button
          className="boton-primario"
          type="button"
          onClick={() => window.location.reload()}
        >
          {t("reventon.recargar", "Recargar la aplicación")}
        </button>

        <p className="tenue texto-mini">
          {t("reventon.siSigue", "Si vuelve a pasar, copia el detalle de abajo y mándamelo.")}
        </p>

        {/* Plegado: el detalle técnico no es para leer, es para copiar.
            Abierto, ocuparía la pantalla y haría ver el problema más
            grave de lo que es. */}
        <details className="reventon-detalle">
          <summary>{t("reventon.detalle", "Detalle técnico")}</summary>
          <pre>{detalle}</pre>
        </details>
      </div>
    );
  }
}

/**
 * Lo que el ErrorBoundary NO puede ver.
 *
 * Errores dentro de manejadores de eventos, de temporizadores, y
 * promesas rechazadas que nadie atrapó. React no los intercepta y hoy
 * desaparecen sin dejar rastro.
 *
 * No cambian la pantalla a propósito: la app sigue andando y llenarla
 * de carteles por algo que quizá no la afectó sería asustar de más. Lo
 * que hacen es quedar en la consola con la misma marca que el resto,
 * que es donde se mira cuando alguien dice "no me anda".
 */
export function mirarLosOtrosErrores() {
  if (typeof window === "undefined") return;

  window.addEventListener("error", (e) => {
    console.error("💥 Brujel: error suelto:", e?.error || e?.message || e);
  });

  window.addEventListener("unhandledrejection", (e) => {
    console.error("💥 Brujel: promesa rechazada sin atrapar:", e?.reason || e);
  });
}
